import { getSmartCollection } from 'api/product/getSmartColleciton';
import { SmartCollectionResponse } from 'api/product/getSmartColleciton/types';
import {
  FetchContentRes,
  fetchContentfulByHandle,
  fetchContentfulShopifyData,
} from 'connectors/contentful';
import { IProductPages } from 'global';
import useSmoothScroll from 'hooks/common/use-smooth-scroll';
import isArray from 'lodash/isArray';
import { ContentfulProvider } from 'modules/content-cms/components/context';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { axios } from 'utils/axios';
import { configureAxiosInstance } from 'utils/configure-axios';
import { hasH1TitleInCdp } from 'utils/contentful';
import getShopId from 'utils/i18n/get-shop-id';
import CollectionModule from '../../modules/collection';

type SegmentationId = string;
type AlterativeRibbonText = string;
export type AbTestRibbonTextObj = {
  [key in SegmentationId]?: AlterativeRibbonText;
};
interface PageProps {
  contentfulShopifyData: FetchContentRes;
  addedProductsData?: Record<string, number>;
  contentfulData: IProductPages;
  handle: string;
  collection: SmartCollectionResponse;
  /** true if page entry has h1 title  */
  hasH1title: boolean;
  tags: string[];
}

const CollectionPage: NextPage<PageProps> = ({
  collection,
  contentfulData,
  handle,
  hasH1title,
  tags,
  contentfulShopifyData,
}) => {
  const router = useRouter();
  const { sortBy } = router.query;
  useSmoothScroll();

  return (
    <>
      <ContentfulProvider
        shopifyData={contentfulShopifyData}
        fallbackData={contentfulData}
        handle={`collections/${handle}`}
      >
        <CollectionModule
          handle={handle}
          collection={collection}
          tags={tags}
          sortBy={sortBy as string}
          hasH1title={hasH1title}
        />
      </ContentfulProvider>
    </>
  );
};

interface UrlQuery extends ParsedUrlQuery {
  all: string;
}

export const getStaticProps: GetStaticProps<PageProps | UrlQuery> = async ({
  locale,
  params,
}) => {
  if (!locale) {
    console.error('Locale is undefined in getStaticProps');
    return { notFound: true, revalidate: true };
  }

  const shopId = getShopId(locale);
  const cleanup = configureAxiosInstance(axios, locale);

  const all = params?.all || [];
  const [handle, encodedTags] = isArray(all) ? all : [all, undefined];
  const tags = encodedTags ? decodeURI(encodedTags).split('+') : [];

  try {
    const [contentfulData, collection] = await Promise.all([
      fetchContentfulByHandle({ handle: `collections/${handle}`, locale }),
      getSmartCollection({ slug: handle }, shopId),
    ]);

    if (
      contentfulData === null ||
      collection === null ||
      collection.title === ''
    ) {
      return { notFound: true, revalidate: true };
    }

    const hasH1title = hasH1TitleInCdp({ contentful: contentfulData });

    cleanup();

    return {
      props: {
        collection,
        // fetchContentfulItem is not typed but we're sure IProductPages is returned here.
        contentfulData: contentfulData as IProductPages,
        contentfulShopifyData: await fetchContentfulShopifyData(
          contentfulData,
          shopId
        ),
        handle,
        hasH1title,
        tags,
      },
      revalidate: 300, // 5 minutes
    };
  } catch (error) {
    console.log(error);
    console.error(
      `Error while prefetching data for product with handle: ${handle}`
    );
    return { notFound: true, revalidate: true };
  }
};
export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default CollectionPage;
